import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  Calendar,
  Clock,
  ArrowLeft,
  ChevronLeft,
  ChevronRight,
  ChevronsUpDown,
  Check,
} from "lucide-react";
import { toast } from "react-hot-toast";
import { Listbox, Transition } from "@headlessui/react";
import { Fragment } from "react";
import { convertLocalTimeToUTC } from "../utils/timeUtils";
import useBooking from "../hooks/stores/useBooking";
import useAuth from "@/hooks/stores/useAuth";
import { slotService } from "@/services";
import { useRegister } from "@/hooks/mutations/auth";
import userIcon from "../assets/icons/user.svg";

const AssistedRecording = () => {
  const navigate = useNavigate();
  const { saveBookingData } = useBooking();
  const { isAuthenticated, setPaymentStatus, setUserRole, setUserFlow } = useAuth();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [currentDate, setCurrentDate] = useState(new Date());
  const [selectedDate, setSelectedDate] = useState(null);
  const [selectedTime, setSelectedTime] = useState("10:00");
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedInterviewer, setSelectedInterviewer] = useState(null);

  // Registration mutation for new users
  const registerMutation = useRegister({
    onSuccess: async (response) => {
      console.log("Registration successful:", response);
      
      const { user } = response;
      
      if (!user) {
        toast.error("Failed to retrieve user information.");
        return;
      }
      
      // Set the user role since it's required for routing
      if (user.roles && user.roles.length > 0) {
        setUserRole(user.roles[0].title);
      }
      
      try {
        // Add a small delay to ensure token is properly set
        await new Promise(resolve => setTimeout(resolve, 100));
        
        // Prepare data for remote slot booking API
        const remoteSlotData = {
          slot_date: formik.values.scheduledDate,
          slot_time: convertLocalTimeToUTC(selectedDate, selectedTime),
          duration: 180,
          location: "remote",
          converted_time: selectedTime,
          preferred_platform: formik.values.preferredPlatform,
          interviewer_id: selectedInterviewer?.id
        };
        
        console.log("Making remote slot booking after registration:", remoteSlotData);
        const slotResponse = await slotService.selectRemoteSlot(remoteSlotData);
        console.log("Remote slot booking successful:", slotResponse);
        
        // Save the slot information to booking context
        const bookingData = {
          ...remoteSlotData,
          slotInfo: slotResponse.slot,
          bookingId: slotResponse.slot.slot_id || slotResponse.slot.id,
          selectedInterviewer: selectedInterviewer,
          bookingType: "remote",
        };
        saveBookingData(bookingData);
        
        // Set payment status to pending for remote booking
        setPaymentStatus("pending");
        
        toast.success("Account created and session booked successfully!");
        
        // Navigate directly to paywall
        navigate("/paywall");
      } catch (error) {
        console.error("Slot booking failed after registration:", error);
        
        let errorMessage = "Account created but booking failed. Please contact support.";
        
        if (error.response && error.response.data) {
          const errorData = error.response.data;
          
          if (typeof errorData.error === 'string') {
            errorMessage = `Booking failed: ${errorData.error}`;
          } else if (typeof errorData.message === 'string') {
            errorMessage = `Booking failed: ${errorData.message}`;
          } else if (typeof errorData.error === 'object') {
            const errorMessages = Object.values(errorData.error).flat();
            errorMessage = `Booking failed: ${errorMessages.join(', ')}`;
          }
        } else if (error.message) {
          errorMessage = `Booking failed: ${error.message}`;
        }
        
        toast.error(errorMessage);
        
        // Still navigate to paywall even if booking fails
        navigate("/paywall");
      } finally {
        setIsSubmitting(false);
      }
    },
    onError: (error) => {
      console.error("Registration failed:", error);
      
      let errorMessage = "Registration failed. Please try again.";
      
      if (error.response && error.response.data) {
        const errorMessages = error.response.data.error;
        
        if (typeof errorMessages === 'object') {
          const message = Array.isArray(errorMessages) 
            ? errorMessages.join(', ') 
            : Object.values(errorMessages).join(', ');
          errorMessage = message;
        } else {
          errorMessage = errorMessages;
        }
      } else if (error.message) {
        errorMessage = error.message;
      }
      
      toast.error(errorMessage);
      setIsSubmitting(false);
    },
  });

  // Updated time slots based on 3-hour duration with 30-minute breaks
  const defaultTimeSlots = [
    { time: "10:00", endTime: "13:00", available: true }, // 10:00-13:00 (3 hours)
    { time: "13:30", endTime: "16:30", available: true }, // 13:30-16:30 (3 hours) + 30 min break
    { time: "17:00", endTime: "20:00", available: true }, // 17:00-20:00 (3 hours) + 30 min break
  ];

  // Load stored data from RemoteCall page
  useEffect(() => {
    const storedData = localStorage.getItem("remoteCallSelections");
    if (storedData) {
      const { selectedInterviewer: interviewerData, slotsData } =
        JSON.parse(storedData);

      // Use the complete interviewer object from RemoteCall page
      setSelectedInterviewer(interviewerData);

      // Set booked slots from the API response
      if (slotsData && slotsData.slots) {
        setBookedSlots(slotsData.slots);
      }
    }

    // Clean up old localStorage data if it exists
    if (localStorage.getItem("assistedBookingData")) {
      localStorage.removeItem("assistedBookingData");
    }
    
    // Set user flow to remote
    setUserFlow('remote');
  }, [setUserFlow]);

  // Utility functions - moved to top to avoid hoisting issues
  const formatDateForDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      day: "numeric",
    });
  };

  const formatDateForAPI = (date) => {
    // Use local date to avoid timezone conversion issues
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, "0");
    const day = String(date.getDate()).padStart(2, "0");
    return `${year}-${month}-${day}`;
  };

  const getMonthYearDisplay = (date) => {
    return date.toLocaleDateString("en-US", {
      month: "long",
      year: "numeric",
    });
  };

  // Generate calendar dates with 2-day minimum future booking
  const generateCalendarDates = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const startDate = new Date(firstDay);
    startDate.setDate(startDate.getDate() - firstDay.getDay());

    const dates = [];
    const today = new Date();
    const minBookingDate = new Date(today);
    minBookingDate.setDate(today.getDate() + 2); // 2 days from today
    minBookingDate.setHours(0, 0, 0, 0);

    for (let i = 0; i < 35; i++) {
      const date = new Date(startDate);
      date.setDate(startDate.getDate() + i);

      // Check if this date has any available slots
      const dateStr = formatDateForAPI(date);
      const bookedTimesForDate = [
        ...new Set(
          bookedSlots
            .filter((slot) => slot.slot_date === dateStr)
            .map((slot) => slot.converted_time.substring(0, 5))
        ),
      ]; // Use converted_time and deduplicate

      const availableSlotsForDate = defaultTimeSlots.filter(
        (slot) => !bookedTimesForDate.includes(slot.time)
      );

      const isFullyBooked =
        availableSlotsForDate.length === 0 &&
        date >= minBookingDate &&
        date.getMonth() === month;

      dates.push({
        date: date,
        day: date.getDate(),
        isCurrentMonth: date.getMonth() === month,
        isPastDate: date < minBookingDate,
        isToday: date.toDateString() === today.toDateString(),
        isMinBookingDate: date.toDateString() === minBookingDate.toDateString(),
        isFullyBooked: isFullyBooked,
      });
    }

    return dates;
  };

  const [calendarDates, setCalendarDates] = useState(
    generateCalendarDates(currentDate)
  );

  useEffect(() => {
    setCalendarDates(generateCalendarDates(currentDate));
  }, [currentDate, bookedSlots]);

  // Get available time slots for selected date
  const getAvailableTimeSlotsForDate = (date) => {
    if (!date) return defaultTimeSlots;

    const dateStr = formatDateForAPI(date);
    const bookedTimesForDate = [
      ...new Set(
        bookedSlots
          .filter((slot) => slot.slot_date === dateStr)
          .map((slot) => slot.converted_time.substring(0, 5))
      ),
    ]; // Use converted_time and deduplicate

    return defaultTimeSlots.map((slot) => ({
      ...slot,
      available: !bookedTimesForDate.includes(slot.time),
    }));
  };

  const navigateMonth = (direction) => {
    const newDate = new Date(currentDate);
    newDate.setMonth(currentDate.getMonth() + direction);
    setCurrentDate(newDate);
  };

  const handleDateSelect = (dateObj) => {
    // Only allow selection of future dates (2+ days from today) in current month that are not fully booked
    if (
      dateObj.isPastDate ||
      !dateObj.isCurrentMonth ||
      dateObj.isFullyBooked
    ) {
      return;
    }
    setSelectedDate(dateObj.date);

    // Reset selected time if it's not available for this date
    const availableSlots = getAvailableTimeSlotsForDate(dateObj.date);
    const isCurrentTimeAvailable = availableSlots.find(
      (slot) => slot.time === selectedTime && slot.available
    );

    if (!isCurrentTimeAvailable) {
      const firstAvailableSlot = availableSlots.find((slot) => slot.available);
      if (firstAvailableSlot) {
        setSelectedTime(firstAvailableSlot.time);
      }
    }
  };

  const handleTimeSlotSelect = (time) => {
    setSelectedTime(time);
  };

  // Get minimum booking date (2 days from today)
  const getMinBookingDate = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return minDate.toISOString().split("T")[0]; // Format as YYYY-MM-DD
  };

  const getMinBookingMessage = () => {
    const today = new Date();
    const minDate = new Date(today);
    minDate.setDate(today.getDate() + 2);
    return `Booking available from ${formatDateForDisplay(minDate)} onwards`;
  };

  const validationSchema = Yup.object({
    fullName: Yup.string()
      .min(2, "Full name must be at least 2 characters")
      .required("Full name is required"),
    email: Yup.string()
      .email("Valid email is required")
      .required("Email is required"),
    password: isAuthenticated ? Yup.string() : Yup.string()
      .min(6, "Password must be at least 6 characters")
      .required("Password is required"),
    confirmPassword: isAuthenticated ? Yup.string() : Yup.string()
      .oneOf([Yup.ref("password"), null], "Passwords must match")
      .required("Confirm Password is required"),
    preferredPlatform: Yup.string().required("Please select a platform"),
    scheduledDate: Yup.string()
      .required("Date is required")
      .test(
        "min-date",
        "Booking must be at least 2 days in advance",
        function (value) {
          if (!value) return false;
          const selectedDate = new Date(value);
          const minDate = new Date();
          minDate.setDate(minDate.getDate() + 2);
          minDate.setHours(0, 0, 0, 0);
          return selectedDate >= minDate;
        }
      ),
    scheduledTime: Yup.string().required("Time is required"),
  });

  const formik = useFormik({
    initialValues: {
      fullName: "",
      email: "",
      password: "",
      confirmPassword: "",
      preferredPlatform: "",
      scheduledDate: "",
      scheduledTime: "",
    },
    validationSchema,
    onSubmit: async (values) => {
      setIsSubmitting(true);
      try {
        if (isAuthenticated) {
          // For existing users, make the slot booking API call directly
          const remoteSlotData = {
            slot_date: formatDateForAPI(selectedDate),
            slot_time: convertLocalTimeToUTC(selectedDate, selectedTime), // UTC time
            duration: 180, // 3 hours in minutes
            location: "remote",
            converted_time: selectedTime, // Local time as originally selected
            preferred_platform: values.preferredPlatform,
            interviewer_id: selectedInterviewer?.id,
          };

          console.log(
            "Making remote slot booking for existing user:",
            remoteSlotData
          );

          const slotResponse = await slotService.selectRemoteSlot(
            remoteSlotData
          );
          console.log("Remote slot booking successful:", slotResponse);

          // Save the slot information to booking context
          const bookingData = {
            ...remoteSlotData,
            slotInfo: slotResponse.slot,
            bookingId: slotResponse.slot.slot_id || slotResponse.slot.id,
            selectedInterviewer: selectedInterviewer,
            bookingType: "remote",
          };
          saveBookingData(bookingData);

          // Set payment status to pending for remote booking
          setPaymentStatus("pending");

          toast.success("Session booked successfully!");

          // Navigate directly to paywall
          navigate("/paywall");
        } else {
          // For new users, create account and book session in one step
          const userData = {
            name: values.fullName.trim(),
            email: values.email.trim(),
            password: values.password.trim(),
            password_confirmation: values.confirmPassword.trim(),
            roles: [2] // Default User role ID
          };
          
          console.log("Creating account and booking session:", userData);
          
          // Call registration mutation which will handle both signup and booking
          registerMutation.mutate(userData);
          return; // Let the mutation handle the rest
        }
      } catch (error) {
        console.error("Booking submission error:", error);

        let errorMessage = "There was an error. Please try again.";

        if (error.response && error.response.data) {
          const errorData = error.response.data;

          if (typeof errorData.error === "string") {
            errorMessage = `Booking failed: ${errorData.error}`;
          } else if (typeof errorData.message === "string") {
            errorMessage = `Booking failed: ${errorData.message}`;
          } else if (typeof errorData.error === "object") {
            // Handle validation errors
            const errorMessages = Object.values(errorData.error).flat();
            errorMessage = `Booking failed: ${errorMessages.join(", ")}`;
          }
        } else if (error.message) {
          errorMessage = `Booking failed: ${error.message}`;
        }

        toast.error(errorMessage);
      } finally {
        // Only set isSubmitting to false for authenticated users here
        // For non-authenticated users, the registerMutation will handle this
        if (isAuthenticated) {
          setIsSubmitting(false);
        }
      }
    },
  });

  // Sync form fields with calendar selection
  useEffect(() => {
    if (selectedDate) {
      formik.setFieldValue("scheduledDate", formatDateForAPI(selectedDate));
    }
  }, [selectedDate]);

  useEffect(() => {
    formik.setFieldValue("scheduledTime", selectedTime);
  }, [selectedTime]);

  const platformOptions = [
    { value: "google", label: "Google Meet" },
    { value: "microsoft", label: "Microsoft Teams" },
    { value: "zoom", label: "Zoom" },
  ];

  // Get the selected platform object for display
  const selectedPlatform =
    platformOptions.find(
      (option) => option.value === formik.values.preferredPlatform
    ) || null;

  const availableTimeSlotsForSelectedDate =
    getAvailableTimeSlotsForDate(selectedDate);

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto px-4 py-8">
        <div className="mb-8">
          <button
            onClick={() => navigate("/remote-call")}
            className="flex items-center text-base font-normal text-foreground hover:text-primary transition-colors mb-4"
          >
            <ArrowLeft className="mr-2" size={20} />
            Back to Remote Call
          </button>

          <h1 className="text-[2.625rem] font-semibold text-foreground mb-2">
            Assisted Recording Session
          </h1>
          <p className="text-base font-normal text-foreground">
            Book a virtual interview session where our AI will process your
            conversation into a structured life story
          </p>
          <p className="text-sm font-normal text-orange-600 mt-2">
            {getMinBookingMessage()}
          </p>
        </div>

        <div className="bg-secondary border border-secondary-border rounded-lg p-6">
          <div className="flex items-center mb-6">
            <Calendar className="mr-2" size={20} />
            <h2 className="text-base font-semibold">Session Details</h2>
          </div>

          <form onSubmit={formik.handleSubmit} className="space-y-3">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label
                  htmlFor="fullName"
                  className="block text-base font-semibold text-foreground mb-1"
                >
                  Full Name
                </label>
                <input
                  id="fullName"
                  name="fullName"
                  type="text"
                  placeholder="Enter your full name"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:font-normal placeholder:text-[#7C797980]"
                  value={formik.values.fullName}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.fullName && formik.errors.fullName && (
                  <p className="mt-2 text-sm font-normal text-red-600">
                    {formik.errors.fullName}
                  </p>
                )}
              </div>

              <div>
                <label
                  htmlFor="email"
                  className="block text-base font-semibold text-foreground mb-1"
                >
                  Email Address
                </label>
                <input
                  id="email"
                  name="email"
                  type="email"
                  placeholder="your@email.com"
                  className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:font-normal placeholder:text-[#7C797980]"
                  value={formik.values.email}
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                />
                {formik.touched.email && formik.errors.email && (
                  <p className="mt-2 text-sm font-normal text-red-600">
                    {formik.errors.email}
                  </p>
                )}
              </div>
            </div>

            {/* Password fields - only show for non-authenticated users */}
            {!isAuthenticated && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label
                    htmlFor="password"
                    className="block text-base font-semibold text-foreground mb-1"
                  >
                    Password
                  </label>
                  <input
                    id="password"
                    name="password"
                    type="password"
                    placeholder="Enter your password"
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:font-normal placeholder:text-[#7C797980]"
                    value={formik.values.password}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.password && formik.errors.password && (
                    <p className="mt-2 text-sm font-normal text-red-600">
                      {formik.errors.password}
                    </p>
                  )}
                </div>
                <div>
                  <label
                    htmlFor="confirmPassword"
                    className="block text-base font-semibold text-foreground mb-1"
                  >
                    Confirm Password
                  </label>
                  <input
                    id="confirmPassword"
                    name="confirmPassword"
                    type="password"
                    placeholder="Confirm your password"
                    className="w-full px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary placeholder:text-sm placeholder:font-normal placeholder:text-[#7C797980]"
                    value={formik.values.confirmPassword}
                    onChange={formik.handleChange}
                    onBlur={formik.handleBlur}
                  />
                  {formik.touched.confirmPassword && formik.errors.confirmPassword && (
                    <p className="mt-2 text-sm font-normal text-red-600">
                      {formik.errors.confirmPassword}
                    </p>
                  )}
                </div>
              </div>
            )}

            <div>
              <label
                htmlFor="preferredPlatform"
                className="block text-base font-semibold text-foreground mb-1"
              >
                Preferred Platform
              </label>
              <div className="relative">
                <Listbox
                  value={formik.values.preferredPlatform}
                  onChange={(value) => {
                    formik.setFieldValue("preferredPlatform", value);
                  }}
                >
                  <div className="relative">
                    <Listbox.Button className="w-full h-[47px] px-4 py-2 bg-background border border-secondary-border rounded-[4px] focus:outline-none focus:ring-2 focus:ring-primary/20 focus:border-primary text-foreground text-left relative cursor-default flex items-center">
                      <span className="block truncate">
                        {selectedPlatform
                          ? selectedPlatform.label
                          : "Select your preferred platform"}
                      </span>
                      <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
                        <ChevronsUpDown
                          className="h-5 w-5 text-gray-400"
                          aria-hidden="true"
                        />
                      </span>
                    </Listbox.Button>
                    <Transition
                      as={Fragment}
                      leave="transition ease-in duration-100"
                      leaveFrom="opacity-100"
                      leaveTo="opacity-0"
                    >
                      <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-[4px] bg-background border border-secondary-border shadow-lg ring-1 ring-black ring-opacity-5 focus:outline-none">
                        {platformOptions.map((platform) => (
                          <Listbox.Option
                            key={platform.value}
                            className={({ active }) =>
                              `relative cursor-default select-none py-2 pl-4 pr-4 ${
                                active
                                  ? "bg-primary/10 text-foreground"
                                  : "text-foreground"
                              }`
                            }
                            value={platform.value}
                          >
                            {({ selected }) => (
                              <>
                                <span
                                  className={`block truncate ${
                                    selected ? "font-medium" : "font-normal"
                                  }`}
                                >
                                  {platform.label}
                                </span>
                                {selected ? (
                                  <span className="absolute inset-y-0 right-0 flex items-center pr-3 text-primary">
                                    <Check
                                      className="h-4 w-4"
                                      aria-hidden="true"
                                    />
                                  </span>
                                ) : null}
                              </>
                            )}
                          </Listbox.Option>
                        ))}
                      </Listbox.Options>
                    </Transition>
                  </div>
                </Listbox>
              </div>
              {formik.touched.preferredPlatform &&
                formik.errors.preferredPlatform && (
                  <p className="mt-2 text-sm font-normal text-red-600">
                    {formik.errors.preferredPlatform}
                  </p>
                )}
            </div>

            {/* Selected Interviewer Display */}
            {selectedInterviewer && (
              <div>
                <label className="block text-base font-semibold text-foreground mb-1">
                  Your Selected Interviewer
                </label>
                <div className="p-4 border-2 border-primary bg-primary/10 rounded-[8px]">
                  <div className="flex items-center space-x-3">
                    <div className="w-10 h-10 bg-primary/20 rounded-full flex items-center justify-center">
                      {selectedInterviewer.image ? (
                        <img
                          src={selectedInterviewer.image}
                          alt={selectedInterviewer.name}
                          className="w-full h-full object-cover rounded-full"
                        />
                      ) : (
                        <img
                          src={userIcon}
                          alt={selectedInterviewer.name}
                          className="w-6 h-6 object-contain"
                        />
                      )}
                    </div>
                    <div>
                      <p className="text-base font-semibold text-foreground">
                        {selectedInterviewer.name}
                      </p>
                      <p className="text-sm font-normal text-foreground">
                        Selected for your session
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Calendar and Time Selection */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Calendar */}
              <div>
                <h3 className="text-base font-semibold text-foreground mb-4">
                  Choose Your Preferred Date
                </h3>

                {/* Calendar Header */}
                <div className="flex items-center justify-between mb-4">
                  <button
                    type="button"
                    className="p-2 hover:bg-muted rounded-lg"
                    onClick={() => navigateMonth(-1)}
                  >
                    <ChevronLeft className="text-muted-foreground" size={20} />
                  </button>
                  <h4 className="text-base font-normal text-foreground">
                    {getMonthYearDisplay(currentDate)}
                  </h4>
                  <button
                    type="button"
                    className="p-2 hover:bg-muted rounded-lg"
                    onClick={() => navigateMonth(1)}
                  >
                    <ChevronRight className="text-muted-foreground" size={20} />
                  </button>
                </div>

                {/* Calendar Grid */}
                <div className="grid grid-cols-7 gap-1 mb-4">
                  {["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"].map(
                    (day) => (
                      <div
                        key={day}
                        className="text-center text-sm font-semibold text-muted-foreground py-2"
                      >
                        {day}
                      </div>
                    )
                  )}
                </div>

                <div className="grid grid-cols-7 gap-1">
                  {calendarDates.slice(0, 35).map((dateObj, index) => (
                    <div
                      key={index}
                      className={`text-center py-2 text-sm rounded-lg transition-colors ${
                        selectedDate &&
                        selectedDate.toDateString() ===
                          dateObj.date.toDateString()
                          ? "bg-primary text-primary-foreground cursor-pointer"
                          : dateObj.isPastDate || !dateObj.isCurrentMonth
                          ? "text-muted-foreground cursor-not-allowed opacity-50"
                          : dateObj.isFullyBooked
                          ? "text-red-500 cursor-not-allowed opacity-50 line-through"
                          : dateObj.isToday
                          ? "bg-secondary border border-secondary-border text-foreground hover:bg-secondary/90 cursor-pointer"
                          : dateObj.isMinBookingDate
                          ? "bg-secondary border border-secondary-border text-foreground hover:bg-secondary/90 cursor-pointer"
                          : "text-foreground hover:bg-secondary border border-secondary-border cursor-pointer"
                      }`}
                      onClick={() => handleDateSelect(dateObj)}
                      title={
                        dateObj.isPastDate
                          ? "Booking must be at least 2 days in advance"
                          : dateObj.isFullyBooked
                          ? "All time slots are booked for this date"
                          : ""
                      }
                    >
                      {dateObj.day}
                    </div>
                  ))}
                </div>
              </div>

              {/* Time Slots */}
              <div>
                <h5 className="text-base font-semibold text-foreground mb-4">
                  Available Times -{" "}
                  {selectedDate
                    ? formatDateForDisplay(selectedDate)
                    : "Select a date"}
                </h5>
                <div className="space-y-3">
                  {availableTimeSlotsForSelectedDate.map((slot) => (
                    <button
                      key={slot.time}
                      type="button"
                      className={`w-full p-4 rounded-lg border transition-colors ${
                        !selectedDate || !slot.available
                          ? "bg-secondary border-secondary-border text-muted-foreground opacity-50 cursor-not-allowed"
                          : selectedTime === slot.time
                          ? "bg-primary border-primary-border text-white"
                          : "bg-secondary border-secondary-border text-foreground hover:bg-secondary/90"
                      }`}
                      onClick={() =>
                        slot.available && handleTimeSlotSelect(slot.time)
                      }
                      disabled={!selectedDate || !slot.available}
                      title={
                        !slot.available
                          ? "This time slot is already booked"
                          : `${slot.time} - ${slot.endTime} (3 hours)`
                      }
                    >
                      <div className="flex flex-col items-center">
                        <span
                          className={`text-base font-normal ${
                            !slot.available ? "line-through" : ""
                          }`}
                        >
                          {slot.time}
                        </span>
                        {!slot.available && (
                          <span className="text-sm font-normal text-red-500 mt-1">
                            Booked
                          </span>
                        )}
                      </div>
                    </button>
                  ))}
                  {selectedDate &&
                    availableTimeSlotsForSelectedDate.every(
                      (slot) => !slot.available
                    ) && (
                      <div className="text-center py-4 text-base font-normal text-red-500">
                        All time slots are booked for this date. Please select
                        another date.
                      </div>
                    )}
                </div>
              </div>
            </div>

            {/* Booking Summary */}
            {selectedDate && (
              <div className="mt-6 p-4 bg-secondary border border-secondary-border rounded-lg">
                <h6 className="text-base font-semibold text-foreground mb-2">
                  Booking Summary
                </h6>
                <p className="text-sm font-normal text-muted-foreground">
                  Date: {formatDateForDisplay(selectedDate)}
                </p>
                <p className="text-sm font-normal text-muted-foreground">
                  Time: {selectedTime} -{" "}
                  {
                    availableTimeSlotsForSelectedDate.find(
                      (s) => s.time === selectedTime
                    )?.endTime
                  }
                </p>
                <p className="text-sm font-normal text-muted-foreground">
                  Duration: 3 hours
                </p>
                <p className="text-sm font-normal text-muted-foreground">
                  Interviewer: {selectedInterviewer?.name}
                </p>
              </div>
            )}

            <button
              type="submit"
              className="w-full py-3 bg-primary text-primary-foreground rounded-[4px] hover:bg-primary/90 transition-colors disabled:opacity-50 disabled:cursor-not-allowed text-base font-semibold"
              disabled={
                isSubmitting ||
                registerMutation.isPending ||
                !selectedDate ||
                availableTimeSlotsForSelectedDate.every(
                  (slot) => !slot.available
                )
              }
            >
              {isSubmitting || registerMutation.isPending 
                ? "Processing..." 
                : "Book Session"}
            </button>
          </form>
        </div>
      </div>
    </div>
  );
};

export default AssistedRecording;
